import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api, getApiBaseUrl, getTenantSlug } from '../api/client';
import OrgSelectionModal from './OrgSelectionModal';
import Button from './ui/Button';
import FormField from './ui/FormField';
import { validate, required, email, minLength } from '../utils/validation';
import PasswordInput from './PasswordInput';
import '../App.css';
import { translateError } from '../utils/errorMessages';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, tenant, isAuthenticated, hasPermission, isSuperAdmin, user, requiresOrgSelection, selectOrganization } = useAuth();

  const [emailValue, setEmailValue] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showOrgSelection, setShowOrgSelection] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);

  // Handle redirect / org selection after login
  React.useEffect(() => {
    if (!isAuthenticated || showOrgSelection) return;

    const activeMemberships = user?.memberships?.filter(m => m.isActive) || [];

    // On main domain (no tenant slug)
    if (!tenant.slug) {
      // Super admin -> super admin dashboard
      if (isSuperAdmin) {
        navigate('/super-admin');
        return;
      }

      // User with memberships -> show org selection to redirect to org subdomain
      if (activeMemberships.length >= 1) {
        setShowOrgSelection(true);
        return;
      }
    }

    // Backend explicitly requires org selection (multi-membership on subdomain)
    if (requiresOrgSelection && activeMemberships.length > 1) {
      setShowOrgSelection(true);
      return;
    }

    // On org subdomain - navigate normally
    if (hasPermission('members:write') || hasPermission('events:write')) {
      navigate('/admin/calendar');
    } else {
      navigate('/dashboard');
    }
  }, [isAuthenticated, requiresOrgSelection, showOrgSelection, hasPermission, isSuperAdmin, tenant.slug, user, navigate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const emailError = validate(emailValue, [
      required(t('validation.emailRequired')),
      email(t('validation.emailInvalid'))
    ]);
    if (emailError) newErrors.email = emailError;

    const passwordError = validate(password, [
      required(t('validation.passwordRequired')),
      minLength(8, t('validation.passwordMinLength'))
    ]);
    if (passwordError) newErrors.password = passwordError;

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await login(emailValue, password);

      // Navigation will happen via useEffect above once isAuthenticated changes
    } catch (err: any) {
      // Check for email not verified error
      if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setEmailNotVerified(true);
        setError(t('auth.emailNotVerified'));
      } else {
        const message = translateError(err, t, 'auth.loginFailed');
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await api.auth.resendVerification(emailValue);
      setVerificationResent(true);
    } catch {
      // Silently handle - the API always returns success for security
    } finally {
      setResendingVerification(false);
    }
  };

  const handleGoogleLogin = () => {
    // Pass tenant slug as query param since this is a redirect (not AJAX with headers)
    const tenantSlug = getTenantSlug();
    const tenantParam = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    window.location.href = `${getApiBaseUrl()}/api/users/google${tenantParam}`;
  };

  // Handle organization selection — OrgSelectionModal handles the redirect
  const handleOrgSelect = async (orgId: string) => {
    await selectOrganization(orgId);
  };

  // Show org selection modal when user needs to pick their organization
  if (showOrgSelection && user?.memberships && user.memberships.filter(m => m.isActive).length >= 1) {
    return (
      <OrgSelectionModal
        memberships={user.memberships}
        onSelect={handleOrgSelect}
      />
    );
  }

  return (
    <div className="page-content">
      <div className="form-container">
        {/* Show organization name if on tenant subdomain */}
        {tenant.name && (
          <div className="organization-badge">
            {tenant.name}
          </div>
        )}

        <h2>{t('auth.signIn')}</h2>

        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}

        {emailNotVerified && !verificationResent && (
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resendingVerification}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--orange, #ff8c00)',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '0.875rem',
              marginBottom: '1rem',
              display: 'block',
            }}
          >
            {resendingVerification ? t('common.loading') : t('auth.resendVerification')}
          </button>
        )}

        {verificationResent && (
          <div className="success-message" role="status">
            {t('auth.verificationResent')}
          </div>
        )}

        <form onSubmit={submitHandler}>
          <FormField
            htmlFor="email"
            label={t('common.email')}
            error={fieldErrors.email}
            required
          >
            <input
              type="email"
              id="email"
              placeholder={t('auth.enterEmail')}
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value);
                if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
              }}
              disabled={isLoading}
            />
          </FormField>
          <FormField
            htmlFor="password"
            label={t('common.password')}
            error={fieldErrors.password}
            required
          >
            <PasswordInput
              id="password"
              placeholder={t('auth.enterPassword')}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: '' }));
              }}
              disabled={isLoading}
            />
          </FormField>
          <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
            <Link
              to="/forgot-password"
              style={{
                fontSize: '0.875rem',
                color: 'var(--orange, #ff8c00)',
                textDecoration: 'none'
              }}
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
          <Button type="submit" fullWidth loading={isLoading}>
            {t('auth.signIn')}
          </Button>
        </form>

        <Link to="/register" className="form-link">
          {t('auth.noAccount')} {t('nav.register')}
        </Link>

        <button
          onClick={handleGoogleLogin}
          className="google-login-button"
          disabled={isLoading}
        >
          {t('auth.loginWithGoogle')}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;
